import React, { useEffect, useRef, useState } from "react";
import {
  DimensionValue,
  LayoutChangeEvent,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { isEqual } from "lodash";
import { config } from "../config";
import {
  StrengthWorkoutData,
  StrengthWorkoutExercise,
  StrengthWorkoutSection,
} from "../providers/ConfigProvider";
import { useTranslation } from "react-i18next";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";
import { PageBullets } from "./PageBullets";

type Orientation = "landscape" | "portrait";

const EMPTY_PAGES: StrengthWorkoutSection[][] = [];
const VERTICAL_COLUMNS = 3;
const SUPERSET_CONNECTOR_GAP = 16;

type ExerciseGroup = {
  key: string;
  exercises: StrengthWorkoutExercise[];
  showConnector: boolean;
};

const groupExercises = (
  exercises: StrengthWorkoutExercise[],
  highlightSupersets: boolean,
): ExerciseGroup[] => {
  if (!highlightSupersets) {
    return exercises.map((exercise, index) => ({
      key: `exercise-${index}`,
      exercises: [exercise],
      showConnector: false,
    }));
  }

  const groups: ExerciseGroup[] = [];
  exercises.forEach((exercise, index) => {
    const last = groups[groups.length - 1];
    const groupKey = exercise.supersetGroup?.trim() ?? "";
    const lastGroupKey = last?.exercises[0]?.supersetGroup?.trim() ?? "";

    if (last != null && groupKey.length > 0 && groupKey === lastGroupKey) {
      last.exercises.push(exercise);
      last.showConnector = last.exercises.length > 1;
      return;
    }

    groups.push({
      key: `exercise-${index}`,
      exercises: [exercise],
      showConnector: false,
    });
  });
  return groups;
};

const chunkRows = (
  sections: StrengthWorkoutSection[],
  orientation: Orientation,
): StrengthWorkoutSection[][] => {
  if (orientation === "landscape" || sections.length === 0) {
    return [sections];
  }

  const rows: StrengthWorkoutSection[][] = [];
  for (let index = 0; index < sections.length; index += VERTICAL_COLUMNS) {
    rows.push(sections.slice(index, index + VERTICAL_COLUMNS));
  }
  return rows;
};

const getColumnCount = (
  sectionCount: number,
  orientation: Orientation,
): number => {
  if (sectionCount === 0) {
    return 1;
  }
  if (orientation === "landscape") {
    return sectionCount;
  }
  return Math.min(VERTICAL_COLUMNS, sectionCount);
};

const useContentStable = <T,>(value: T): T => {
  const ref = useRef(value);
  if (!isEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
};

const useOverflowTicker = () => {
  const theme = useTheme();
  const scrollView = useRef<ScrollView>(null);
  const scrollAnimation = useRef(0);
  const [outerHeight, setOuterHeight] = useState<number>();
  const [innerHeight, setInnerHeight] = useState<number>();

  useEffect(() => {
    if (innerHeight == null || outerHeight == null) {
      return;
    }

    const rawOverflow = innerHeight - outerHeight;
    const overflowThreshold = theme.scaled(5);
    const toValue = rawOverflow > overflowThreshold ? rawOverflow : 0;

    if (toValue === 0) {
      scrollAnimation.current = 0;
      scrollView.current?.scrollTo({ y: 0, animated: false });
      return;
    }

    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;

    const startScrolling = () => {
      scrollAnimation.current = 0;
      scrollView.current?.scrollTo({ y: 0, animated: false });

      timeout = setTimeout(() => {
        interval = setInterval(() => {
          if (scrollAnimation.current >= toValue) {
            clearInterval(interval);
            timeout = setTimeout(startScrolling, config.textCards.endDelay);
            return;
          }

          scrollAnimation.current += Math.max(theme.scaled(1), 1);
          scrollView.current?.scrollTo({
            y: scrollAnimation.current,
            animated: false,
          });
        }, 1000 / config.textCards.fps);
      }, config.textCards.startDelay);
    };

    startScrolling();

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [innerHeight, outerHeight, theme]);

  const onOuterLayout = (event: LayoutChangeEvent) => {
    setOuterHeight(event.nativeEvent.layout.height);
  };

  const onInnerLayout = (event: LayoutChangeEvent) => {
    setInnerHeight(event.nativeEvent.layout.height);
  };

  return { scrollView, onOuterLayout, onInnerLayout };
};

type FontTier = {
  section: number;
  exercise: number;
  prescription: number;
};

const getFontTier = (
  theme: ThemeOptions,
  orientation: Orientation,
  sectionCount: number,
): FontTier => {
  const useCompactTier =
    orientation === "landscape" ? sectionCount >= 5 : sectionCount >= 3;

  return useCompactTier
    ? {
        section: theme.scaled(24),
        exercise: theme.scaled(20),
        prescription: theme.scaled(20),
      }
    : {
        section: theme.scaled(34),
        exercise: theme.scaled(24),
        prescription: theme.scaled(24),
      };
};

const makeStyles = (theme: ThemeOptions, fontTier: FontTier) =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    quote: {
      color: theme.backgroundText,
      fontSize: fontTier.exercise,
      fontStyle: "italic",
      textAlign: "center",
      paddingHorizontal: theme.spacing.regular,
      paddingTop: theme.spacing.small,
      paddingBottom: theme.spacing.medium,
    },
    quoteDivider: {
      height: theme.borderWidth.small,
      backgroundColor: theme.toRgba(theme.backgroundText, 0.2),
    },
    board: {
      flex: 1,
      flexDirection: "column",
      paddingHorizontal: theme.spacing.regular,
      paddingTop: theme.spacing.large,
      paddingBottom: theme.spacing.medium,
    },
    row: {
      flex: 1,
      flexDirection: "row",
    },
    section: {
      height: "100%",
      paddingHorizontal: theme.spacing.medium,
    },
    sectionBody: {
      flex: 1,
    },
    sectionHeader: {
      color: theme.backgroundText,
      fontSize: fontTier.section,
      fontWeight: "600",
      marginBottom: theme.spacing.small,
    },
    sectionInstruction: {
      color: theme.backgroundText,
      fontSize: fontTier.prescription,
      fontStyle: "italic",
      marginBottom: theme.spacing.medium,
    },
    exercise: {
      marginBottom: theme.spacing.medium,
    },
    exerciseLast: {
      marginBottom: 0,
    },
    supersetGroup: {
      flexDirection: "row",
      alignItems: "stretch",
      marginBottom: theme.spacing.medium,
    },
    supersetConnector: {
      width: theme.borderWidth.regular,
      backgroundColor: theme.backgroundText,
      marginRight: theme.scaled(SUPERSET_CONNECTOR_GAP),
    },
    supersetExercises: {
      flex: 1,
    },
    exerciseNameRow: {
      flexDirection: "row",
      alignItems: "flex-start",
    },
    exerciseName: {
      color: theme.backgroundText,
      fontSize: fontTier.exercise,
      fontWeight: "600",
    },
    exerciseNameText: {
      flex: 1,
      marginLeft: theme.spacing.small,
    },
    exerciseInstruction: {
      color: theme.backgroundText,
      fontSize: fontTier.prescription,
      fontStyle: "italic",
      marginTop: theme.spacing.xsmall,
    },
    prescription: {
      color: theme.toRgba(theme.backgroundText, 0.6),
      fontSize: fontTier.prescription,
      marginTop: theme.spacing.xsmall,
    },
    fallback: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: theme.spacing.regular,
    },
    fallbackIcon: {
      width: theme.scaled(80),
      height: theme.scaled(80),
      borderRadius: theme.scaled(40),
      backgroundColor: theme.backgroundText,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: theme.spacing.medium,
    },
    fallbackIconMark: {
      color: theme.background,
      fontSize: theme.scaled(48),
      fontWeight: "700",
      lineHeight: theme.scaled(52),
    },
    fallbackTitle: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.h3,
      fontWeight: "700",
      textAlign: "center",
      marginBottom: theme.spacing.small,
    },
    fallbackMessage: {
      color: theme.toRgba(theme.backgroundText, 0.65),
      fontSize: theme.fontSize.large,
      textAlign: "center",
      maxWidth: theme.scaled(720),
    },
  });

const Exercise = ({
  exercise,
  styles,
  last = false,
}: {
  exercise: StrengthWorkoutExercise;
  styles: ReturnType<typeof makeStyles>;
  last?: boolean;
}) => {
  const rawLabel = exercise.label.trim();
  const label =
    rawLabel.length > 0 && !rawLabel.endsWith(".") ? `${rawLabel}.` : rawLabel;

  return (
    <View style={[styles.exercise, last && styles.exerciseLast]}>
      <View style={styles.exerciseNameRow}>
        {label.length > 0 && <Text style={styles.exerciseName}>{label}</Text>}
        <Text style={[styles.exerciseName, styles.exerciseNameText]}>
          {exercise.name}
        </Text>
      </View>
      {exercise.instruction != null && exercise.instruction.length > 0 && (
        <Text style={styles.exerciseInstruction}>{exercise.instruction}</Text>
      )}
      {exercise.prescription.map((prescription, index) => (
        <Text
          key={`${exercise.label}-prescription-${index}`}
          style={styles.prescription}
        >
          {prescription}
        </Text>
      ))}
    </View>
  );
};

const Section = ({
  section,
  styles,
  width,
  highlightSupersets,
}: {
  section: StrengthWorkoutSection;
  styles: ReturnType<typeof makeStyles>;
  width: DimensionValue;
  highlightSupersets: boolean;
}) => {
  const groups = groupExercises(section.exercises, highlightSupersets);
  const { scrollView, onOuterLayout, onInnerLayout } = useOverflowTicker();

  return (
    <View style={[styles.section, { width }]}>
      <Text style={styles.sectionHeader}>{section.name}</Text>
      {section.instruction != null && section.instruction.length > 0 && (
        <Text style={styles.sectionInstruction}>{section.instruction}</Text>
      )}
      <ScrollView
        ref={scrollView}
        style={styles.sectionBody}
        showsVerticalScrollIndicator={false}
        scrollEnabled={false}
        onLayout={onOuterLayout}
      >
        <View onLayout={onInnerLayout}>
          {groups.map((group) => {
            const exercises = group.exercises.map((exercise, index) => (
              <Exercise
                key={`${section.id}-${group.key}-${index}`}
                exercise={exercise}
                styles={styles}
                last={
                  group.showConnector && index === group.exercises.length - 1
                }
              />
            ));

            if (!group.showConnector) {
              return (
                <View key={`${section.id}-${group.key}`}>{exercises}</View>
              );
            }

            return (
              <View
                key={`${section.id}-${group.key}`}
                style={styles.supersetGroup}
              >
                <View style={styles.supersetConnector} />
                <View style={styles.supersetExercises}>{exercises}</View>
              </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
};

export const StrengthWorkoutBoard = ({
  data,
  orientation,
  onLoopComplete,
}: {
  data: StrengthWorkoutData | null;
  orientation: Orientation;
  onLoopComplete: () => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation("StrengthWorkoutBoard");
  const hasSelection = data?.selection != null;
  const pages = useContentStable(data?.selection?.pages ?? EMPTY_PAGES);
  const pageDurationMs = (data?.selection?.pageDurationSeconds ?? 0) * 1000;
  const [currentIndex, setCurrentIndex] = useState(0);
  const onLoopCompleteRef = useRef(onLoopComplete);
  onLoopCompleteRef.current = onLoopComplete;

  useEffect(() => {
    setCurrentIndex(0);
  }, [pages]);

  useEffect(() => {
    if (!hasSelection || pages.length === 0) {
      // Nothing to show — fire a fallback completion so a looped playlist
      // item can still advance instead of stalling forever.
      const timeout = setTimeout(
        () => onLoopCompleteRef.current(),
        config.notFoundTimeout,
      );
      return () => clearTimeout(timeout);
    }

    // A valid selection with no pageDurationSeconds is a single-page board
    // (the API requires null for ≤6 sections); give it a readable default
    // dwell rather than the not-found fallback.
    const dwellMs =
      pageDurationMs > 0 ? pageDurationMs : config.strength.defaultPageDuration;

    if (currentIndex >= pages.length) {
      setCurrentIndex(0);
      return;
    }

    const timeout = setTimeout(() => {
      const next = currentIndex + 1 >= pages.length ? 0 : currentIndex + 1;
      if (next === 0) {
        onLoopCompleteRef.current();
      }
      setCurrentIndex(next);
    }, dwellMs);

    return () => clearTimeout(timeout);
  }, [currentIndex, pages, pageDurationMs, hasSelection]);

  // A poll can shrink `pages` below `currentIndex`; clamp for this render
  // rather than committing an empty frame before the [pages] effect resets.
  const pageIndex = currentIndex < pages.length ? currentIndex : 0;
  const sections = pages[pageIndex] ?? [];
  const rows = chunkRows(sections, orientation);
  const columnCount = getColumnCount(sections.length, orientation);
  const centerRows = orientation === "landscape" || rows.length <= 1;
  const sectionWidth: DimensionValue = `${100 / columnCount}%`;
  const fontTier = getFontTier(theme, orientation, sections.length);
  const styles = makeStyles(theme, fontTier);
  const quote = data?.selection?.quote?.trim() ?? "";
  const highlightSupersets = data?.selection?.highlightSupersets ?? false;

  if (!hasSelection) {
    return (
      <View style={styles.fallback}>
        <View style={styles.fallbackIcon}>
          <Text style={styles.fallbackIconMark}>!</Text>
        </View>
        <Text style={styles.fallbackTitle}>{t("No Workout Selected")}</Text>
        <Text style={styles.fallbackMessage}>
          {t(
            "Select a workout from the Digital Display tab in S&C to display it here",
          )}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {quote.length > 0 && (
        <>
          <Text style={styles.quote}>{quote}</Text>
          <View style={styles.quoteDivider} />
        </>
      )}
      <View style={styles.board}>
        {rows.map((row, rowIndex) => (
          <View
            key={`row-${rowIndex}`}
            style={[
              styles.row,
              { justifyContent: centerRows ? "center" : "flex-start" },
            ]}
          >
            {row.map((section) => (
              <Section
                key={section.id}
                section={section}
                styles={styles}
                width={sectionWidth}
                highlightSupersets={highlightSupersets}
              />
            ))}
          </View>
        ))}
      </View>
      {pages.length > 1 && (
        <PageBullets total={pages.length} currentIndex={pageIndex} />
      )}
    </View>
  );
};
